"use client"

import React from "react"
import styled, { css } from "styled-components"
import { Box, common } from "@jobintrans/base-ui.components.atoms._atoms"
import { BaseStyles } from "@jobintrans/base-ui.types.base-styles"

const RadioSlider = ({
  name,
  value,
  onChange,
  options,
  $columnBreakpoint,
  $column,
  $activeColor,
  $activeBackground,
  $boxShadow = true,
  ...rest
}: BaseStyles & {
  name: string
  value: string
  onChange: (e: { name: string; value: string }) => void
  options: string[][]
  $column?: boolean
  $activeColor?: string
  $activeBackground?: string
  $columnBreakpoint?: number
  $boxShadow?: boolean
}) => {
  return (
    <Wrapper $boxShadow={$boxShadow} {...rest}>
      <Content $column={$column} $columnBreakpoint={$columnBreakpoint}>
        {value && value !== "" && (
          <Background
            $column={$column}
            $columnBreakpoint={$columnBreakpoint}
            $activeBackground={$activeBackground}
            $active={options.findIndex(el => el[1] === value)}
            $length={options.length}
          />
        )}
        {options.map(el => {
          const optionValue = el[1]
          const text = el[0]

          return (
            <Option
              key={optionValue}
              type="button"
              className="option"
              $active={value === optionValue}
              onClick={() =>
                onChange({
                  name,
                  value: optionValue,
                })
              }
              $activeColor={$activeColor}
              $flexDirection="row"
            >
              {text}
            </Option>
          )
        })}
      </Content>
    </Wrapper>
  )
}

export default RadioSlider

const Background = styled(Box)<
  BaseStyles & {
    $length: number
    $active: number
    $column?: boolean
    $activeBackground?: string
    $columnBreakpoint?: number
  }
>`
  background-color: ${({ theme, $activeBackground = "primary" }) =>
    theme.color[$activeBackground] ?? $activeBackground};
  width: calc(100% / ${({ $length }) => $length});
  height: 100%;
  position: absolute;
  left: ${({ $length, $active }) => `calc((100% / ${$length}) * ${$active})`};
  top: 0;
  z-index: 0;
  transition: left 0.5s, top 0.5s;
  border-radius: ${({ theme }) => theme.variable.borderRadiusButton};

  ${({ $length, $columnBreakpoint, $active }) =>
    $columnBreakpoint &&
    css`
      @media (max-width: ${$columnBreakpoint}px) {
        width: 100%;
        height: calc(100% / ${length});
        left: 0 !important;
        top: ${`calc((100% / ${length}) * ${$active})`};
      }
    `};

  ${({ $length, $column, $active }) =>
    $column &&
    css`
      width: 100%;
      height: calc(100% / ${$length});
      left: 0 !important;
      top: ${`calc((100% / ${$length}) * ${$active})`};
    `};
`

const Option = styled.button<{
  $active: boolean
  $activeColor?: string
  $flexDirection?: string
}>`
  cursor: pointer;
  padding: 0 ${({ theme }) => theme.space.S};
  height: 45px;
  min-height: 45px;
  font-size: ${({ theme }) => theme.font.M};
  font-weight: ${({ theme }) => theme.font.normal};
  color: ${({ theme }) => theme.color.second};
  border: 0px;
  border-radius: ${({ theme }) => theme.variable.borderRadiusButton};
  color: ${({ theme, $active, $activeColor = "white" }) =>
    $active ? theme.color[$activeColor] ?? $activeColor : theme.color.second};
  transition: color 0.3s;
  background: none;
  z-index: 1;
  position: relative;
  flex: 1 1;
  margin: 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: ${({ $flexDirection = "row" }) => $flexDirection};

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `};

  @media (max-width: 400px) {
    font-size: ${({ theme }) => theme.font.S};
  }
`

const Content = styled(Box)<
  BaseStyles & {
    $column?: boolean
    $columnBreakpoint?: number
  }
>`
  width: 100%;
  display: flex;

  ${({ $columnBreakpoint }) =>
    $columnBreakpoint &&
    css`
      @media (max-width: ${$columnBreakpoint}px) {
        flex-direction: column;
      }
    `};

  ${({ $column }) =>
    $column &&
    css`
      flex-direction: column;
    `};
`

const Wrapper = styled(Box)<
  BaseStyles & {
    $boxShadow: boolean
  }
>`
  ${common};
  background: ${({ theme, $background = "white" }) => theme.color[$background]};
  width: ${({ $width }) => $width || "100%"};
  max-width: ${({ $maxWidth }) => $maxWidth || "none"};
  padding: 5px;
  border-radius: calc(${({ theme }) => theme.variable.borderRadiusButton} + 5px);
  position: relative;
  display: flex;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }

  ${({ theme, $boxShadow }) =>
    $boxShadow &&
    css`
      box-shadow: ${theme.variable.boxShadow};
    `}
`
